import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import ProductTable from "./ProductTable";
import * as XLSX from "xlsx";
import Sidebar from "./Sidebar";
import Nav from "./Nav";
import AddProductModal from "./AddProductModal";
import Spinner from "./Spinner";
import Swal from "sweetalert2";
import "bootstrap-icons/font/bootstrap-icons.css";
import {
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getFirestore,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { getSubCollection } from "../utils/getSubCollection";
import moment from "moment";
import { useNavContext } from "../context/NavContext";
import DetailsArticuloModal from "./DetailsArticuloModal";
import { normalizeImages } from "../utils/storage";
import ProductButton from "./ProductButton";
import Breadcrumb from "./Breadcrumb";

moment.locale("es");

// ESTILO GENÉRICO PARA BOTONES
const buttonStyle = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  padding: "8px 14px",
  fontSize: "14px",
  borderRadius: "6px",
  cursor: "pointer",
};

function parseCost(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    return parseFloat(value.replace(/[^0-9.-]+/g, "")) || 0;
  }
  return 0;
}

const ArticulosCompra = () => {
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user.uid;
  const productosCol = useMemo(
    () => getSubCollection(ownerUid, "productosProveedores"),
    [ownerUid]
  );
  const uploaderUid = user?.uid;
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const gearRef = useRef(null);
  const filterRef = useRef(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailProd, setDetailProd] = useState(null);
  const { searchTerm, setSearchTerm, proximasAVencer, proximosEventos } =
    useNavContext();
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  const isAnyProductSelected = Object.values(selectedProducts).some(Boolean);
  const [adjustOpen, setAdjustOpen] = useState(false);

  // effect global para cerrar cuando se clickea fuera
  useEffect(() => {
    function handleClickOutside(e) {
      // si hay menú abierto y el click no fue dentro de gearRef, ciérralo
      if (
        showOptionsMenu &&
        gearRef.current &&
        !gearRef.current.contains(e.target)
      ) {
        setShowOptionsMenu(false);
      }
      // lo mismo para el menú de filtrar
      if (
        showFilterMenu &&
        filterRef.current &&
        !filterRef.current.contains(e.target)
      ) {
        setShowFilterMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showOptionsMenu, showFilterMenu]);

  // Efecto: cargas Firestore
  useEffect(() => {
    if (!ownerUid) return;
    setIsLoading(true);
    const unsub = onSnapshot(
      productosCol,
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setProducts(docs);
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );
    return () => unsub();
  }, [productosCol, ownerUid]);

  // Filtrado por searchTerm
  const filterProducts = useCallback(
    (term) => {
      const lower = term.toLowerCase();
      const byText = products.filter((p) =>
        [p.nombre, p.proveedor, p.categoria, p.numeroDeParteProveedor].some(
          (f) => f?.toString().toLowerCase().includes(lower)
        )
      );
      setFilteredProducts(byText);
    },
    [products]
  );

  useEffect(() => {
    filterProducts(searchTerm);
  }, [searchTerm, filterProducts]);

  // En el padre (ArticulosVenta.jsx)
  const handleDuplicateProduct = async (id) => {
    const original = products.find((p) => p.id === id);
    if (!original) return;

    const { isConfirmed } = await Swal.fire({
      title: "¿Duplicar este producto?",
      text: original.nombre || "",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, duplicar",
      cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;

    try {
      // 1) Copia segura sin campos que no debes reusar
      const { id: _omit, fechaCreacion: _omit2, ...data } = original;

      // 2) Normaliza arrays/objetos y garantiza imagen principal
      const base = {
        ...data,
        costo: parseCost(data.costo),
        imagenes:
          Array.isArray(data.imagenes) && data.imagenes.length
            ? [...data.imagenes]
            : data.imagen
            ? [data.imagen]
            : [],
        imagen:
          data.imagen ||
          (Array.isArray(data.imagenes) ? data.imagenes[0] : null),
        variantes: data.variantes ? { ...data.variantes } : {},
        etiquetas: Array.isArray(data.etiquetas) ? [...data.etiquetas] : [],
      };

      // 3) Sube a Storage si alguna viene en base64; si ya es URL la deja tal cual
      const withUrls = await normalizeImages(base, uploaderUid);

      // 4) Payload final a Firestore (solo URLs)
      const payload = {
        nombre: (withUrls.nombre || "").trim(),
        modelo: withUrls.modelo || "",
        numeroDeParteProveedor: withUrls.numeroDeParteProveedor || "",
        categoria: withUrls.categoria || "",
        descripcion: withUrls.descripcion || "",
        especificaciones: withUrls.especificaciones || "",
        notas: withUrls.notas || "",
        costo: withUrls.costo ?? 0,
        impuesto: withUrls.impuesto || "",
        existencias: withUrls.existencias ?? 0,
        etiquetas: withUrls.etiquetas || [],
        imagen: withUrls.imagen || withUrls.imagenes?.[0] || null,
        imagenes: withUrls.imagenes || [],
        variantes: withUrls.variantes || {},
        fechaCreacion: moment().toISOString(),
      };

      // 5) Crea el duplicado
      await addDoc(productosCol, payload);
      Swal.fire("Duplicado", "Se duplicó el producto.", "success");
    } catch (err) {
      console.error(err);
      Swal.fire(
        "Error",
        err.message || "No se pudo duplicar el producto.",
        "error"
      );
    }
  };

  const handleAddProduct = async (newProduct) => {
    try {
      // 1) Subir a Storage cualquier imagen que venga en base64 y obtener URLs
      const withUrls = await normalizeImages(
        { ...newProduct, costo: parseCost(newProduct.costo) },
        uploaderUid
      );

      // 2) Armar payload final (solo valores limpios + URLs)
      const payload = {
        nombre: (withUrls.nombre || "").trim(),
        modelo: withUrls.modelo || "",
        numeroDeParteProveedor: withUrls.numeroDeParteProveedor || "",
        categoria: withUrls.categoria || "",
        descripcion: withUrls.descripcion || "",
        especificaciones: withUrls.especificaciones || "",
        notas: withUrls.notas || "",
        costo: withUrls.costo ?? 0,
        impuesto: withUrls.impuesto || "",
        existencias: withUrls.existencias ?? 0,
        etiquetas: Array.isArray(withUrls.etiquetas) ? withUrls.etiquetas : [],
        imagen: withUrls.imagen || withUrls.imagenes?.[0] || null, // URL en Storage
        imagenes: Array.isArray(withUrls.imagenes) ? withUrls.imagenes : [], // URLs en Storage
        variantes: withUrls.variantes || {},
        fechaCreacion: moment().toISOString(),
      };

      // 3) Guardar en Firestore
      await addDoc(productosCol, payload);

      setIsModalOpen(false);
      Swal.fire("Guardado", "Producto agregado correctamente.", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.message || "No se pudo guardar.", "error");
    }
  };

  const handleEditProduct = async (prod) => {
    try {
      // 1) Normaliza/corrige datos base
      const safeImages = Array.isArray(prod.imagenes)
        ? prod.imagenes.filter(Boolean)
        : [];

      // Si no mandaron arreglo pero sí hay imagen principal, consérvala
      if (safeImages.length === 0 && prod.imagen) {
        safeImages.push(prod.imagen);
      }

      const base = {
        ...prod,
        costo: parseCost(prod.costo),
        imagenes: safeImages,
        etiquetas: Array.isArray(prod.etiquetas) ? prod.etiquetas : [],
      };

      // 2) Sube a Storage cualquier imagen base64
      //    (si ya es URL pública, la deja tal cual)
      const withUrls = await normalizeImages(base, uploaderUid);

      // 3) Payload final a Firestore (solo valores limpios + URLs)
      const payload = {
        nombre: (withUrls.nombre || "").trim(),
        modelo: withUrls.modelo || "",
        numeroDeParteProveedor: withUrls.numeroDeParteProveedor || "",
        categoria: withUrls.categoria || "",
        descripcion: withUrls.descripcion || "",
        especificaciones: withUrls.especificaciones || "",
        notas: withUrls.notas || "",
        costo: withUrls.costo ?? 0,
        impuesto: withUrls.impuesto || "",
        existencias: withUrls.existencias ?? 0,
        etiquetas: withUrls.etiquetas || [],
        imagen: withUrls.imagen || withUrls.imagenes?.[0] || null, // URL en Storage
        imagenes: withUrls.imagenes || [], // URLs en Storage
        variantes: withUrls.variantes || {},
      };

      // 4) Actualiza el documento
      await updateDoc(doc(productosCol, prod.id), payload);

      setIsModalOpen(false);
      Swal.fire(
        "Actualizado",
        "El producto se actualizó correctamente.",
        "success"
      );
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudo actualizar", "error");
    }
  };

  const handleDeleteProduct = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro que quieres eliminar este producto?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
    });

    if (result.isConfirmed) {
      try {
        const docRef = doc(
          getFirestore(),
          "usuarios",
          ownerUid,
          "productosProveedores",
          id
        );
        await deleteDoc(docRef);
        Swal.fire(
          "¡Eliminado!",
          "El producto fue eliminado correctamente.",
          "success"
        );
      } catch (err) {
        Swal.fire("Error", "No se pudo eliminar", "error");
      }
    }
  };

  const handleDeleteSelected = async () => {
    const ids = Object.keys(selectedProducts).filter(
      (k) => selectedProducts[k]
    );
    if (!ids.length) return;

    const result = await Swal.fire({
      title: `¿Eliminar ${ids.length} producto(s) seleccionados?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
    });

    if (result.isConfirmed) {
      try {
        await Promise.all(ids.map((i) => deleteDoc(doc(productosCol, i))));
        setSelectedProducts({});
        Swal.fire(
          "¡Eliminado!",
          "Productos eliminados correctamente.",
          "success"
        );
      } catch {
        Swal.fire("Error", "Ocurrió un problema al eliminar", "error");
      }
    }
  };

  // Exportar / Importar Excel
  const handleExportClick = () => {
    const ws = XLSX.utils.json_to_sheet(
      filteredProducts.map((p) => ({
        Descripción: p.nombre,
        "No. Parte": p.numeroDeParteProveedor,
        Precio: p.costo,
        Categoría: p.categoria,
        Proveedor: p.proveedor,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Productos");
    XLSX.writeFile(wb, `export_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return Swal.fire("Error", "Sin archivo", "error");
    const reader = new FileReader();
    reader.onload = async () => {
      const wb = XLSX.read(new Uint8Array(reader.result), { type: "array" });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);

      let added = 0;
      for (let row of rows) {
        // ejemplo: buscar duplicados
        const q = query(
          productosCol,
          where("numeroDeParteProveedor", "==", row.CÓDIGO)
        );
        const snap = await getDocs(q);
        if (snap.empty) {
          await addDoc(productosCol, {
            nombre: row.DESCRIPCIÓN,
            costo: parseCost(row["PRECIO UNITARIO"]),
            // etc…
          });
          added++;
        }
      }
      Swal.fire(
        "Importación exitosa",
        `Se agregaron ${added} artículos.`,
        "success"
      );
    };
  };

  const handleImportClick = () => fileInputRef.current.click();
  const toggleFilterMenu = () => setShowFilterMenu((v) => !v);
  const toggleOptionsMenu = () => setShowOptionsMenu((v) => !v);

  useEffect(() => {
    const abrirModalHandler = () => {
      setEditingProduct(null);
      setIsModalOpen(true);
    };
    window.addEventListener("abrir-modal-producto", abrirModalHandler);
    return () =>
      window.removeEventListener("abrir-modal-producto", abrirModalHandler);
  }, []);

  return (
    <>
      <div className="articulos-venta">
        <Nav
          handleSearch={setSearchTerm}
          proximasAVencer={proximasAVencer}
          proximosEventos={proximosEventos}
        />

        <div className="task-filters">
          <div
            className="task-filters-right"
            style={{ width: "100%", flexWrap: "wrap" }}
          >
            <div
              style={{
                flexBasis: "100%", // ocupa toda la fila
                padding: "0 4px", // mismo gutter que usas
              }}
            >
              <Breadcrumb />
            </div>
            {/* Engranaje “suelto” + divisor */}
            <div
              ref={gearRef}
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                marginLeft: 6,
                marginRight: 10,
                paddingRight: 10,
                borderRight: "1px solid #E5E7EB",
                height: 34,
                marginTop: 4,
              }}
            >
              <button
                onClick={toggleOptionsMenu}
                disabled={isAnyProductSelected}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  fontSize: 20,
                  color: isAnyProductSelected ? "#A1A1AA" : "#6B7280",
                  cursor: isAnyProductSelected ? "default" : "pointer",
                }}
              >
                <i className="bi bi-gear" />
              </button>

              {showOptionsMenu && !isAnyProductSelected && (
                <div
                  className="dropdown-options"
                  ref={dropdownRef}
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    zIndex: 9999,
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: 6,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {/* Exportar */}
                  <p
                    onClick={() => {
                      handleExportClick();
                      setShowOptionsMenu(false);
                    }}
                    style={{
                      margin: 0,
                      padding: "8px 12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#F3F4F6")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <i
                      className="bi bi-box-arrow-up-right"
                      style={{ fontSize: 16, color: "#6B7280" }}
                    />
                    Exportar
                  </p>

                  {/* Importar */}
                  <p
                    onClick={() => {
                      handleImportClick();
                      setShowOptionsMenu(false);
                    }}
                    style={{
                      margin: 0,
                      padding: "8px 12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#F3F4F6")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <i
                      className="bi bi-box-arrow-in-down"
                      style={{ fontSize: 16, color: "#6B7280" }}
                    />
                    Importar
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".xlsx,.xls"
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                  />

                  {/* Ajustar precios */}
                  <p
                    onClick={() => {
                      setAdjustOpen(true);
                      setShowOptionsMenu(false);
                    }}
                    style={{
                      margin: 0,
                      padding: "8px 12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#F3F4F6")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <i
                      className="bi bi-sliders"
                      style={{ fontSize: 16, color: "#6B7280" }}
                    />
                    Ajustar precios
                  </p>
                </div>
              )}
            </div>

            {/* Buscador */}
            <div
              className="buscador-pedidos"
              style={{ position: "relative", marginRight: 10, flexShrink: 0 }}
            >
              <i
                className="bi bi-search"
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#6B7280",
                  fontSize: 16,
                  pointerEvents: "none",
                }}
              />
              <input
                type="text"
                placeholder="Buscar productos..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  filterProducts(e.target.value);
                }}
                style={{
                  width: 212,
                  height: 36,
                  padding: "0 12px 0 36px",
                  borderRadius: 6,
                  backgroundColor: "#F8FAFB",
                  border: "1px solid #E6EDFF",
                  fontSize: 14,
                }}
              />
            </div>

            {/* Filtrar */}
            <div
              ref={filterRef}
              style={{ position: "relative", marginRight: 10, flexShrink: 0 }}
            >
              <button
                onClick={toggleFilterMenu}
                style={{
                  ...buttonStyle,
                  backgroundColor: "#fff",
                  border: "1px solid #E5E7EB",
                  color: "#374151",
                  fontWeight: 600,
                }}
              >
                <i
                  className="bi bi-filter"
                  style={{ fontSize: 16, fontWeight: 600 }}
                />
                <span style={{ margin: "0 4px", fontWeight: 600 }}>
                  Filtrar
                </span>
                <i
                  className="bi bi-caret-down-fill"
                  style={{ fontSize: 12, fontWeight: 600 }}
                />
              </button>

              {showFilterMenu && (
                <div
                  className="dropdown-options"
                  style={{
                    position: "absolute",
                    top: "calc(100% + 5px)",
                    right: 0,
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: 6,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    minWidth: 160,
                    zIndex: 1000,
                    whiteSpace: "nowrap",
                  }}
                >
                  {/* Por categoría (mismo estilo que engrane) */}
                  <p
                    onClick={() => {
                      // tu handler de categoría
                      // filtrarPorCategoria();
                      setShowFilterMenu(false);
                    }}
                    style={{
                      margin: 0,
                      padding: "8px 12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#F3F4F6")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <i
                      style={{ fontSize: 16, color: "#6B7280" }}
                      className="bi bi-grid-3x3-gap"
                    />
                    Por categoría
                  </p>

                  {/* Por proveedor (mismo estilo que engrane) */}
                  <p
                    onClick={() => {
                      // tu handler de proveedor
                      // filtrarPorProveedor();
                      setShowFilterMenu(false);
                    }}
                    style={{
                      margin: 0,
                      padding: "8px 12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#F3F4F6")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <i
                      style={{ fontSize: 16, color: "#6B7280" }}
                      className="bi bi-shop"
                    />
                    Por proveedor
                  </p>
                </div>
              )}
            </div>

            {/* Controles finales */}
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <button
                onClick={handleDeleteSelected}
                style={{
                  ...buttonStyle,
                  backgroundColor: "#EF4444",
                  color: "#fff",
                  border: "none",
                  opacity: isAnyProductSelected ? 1 : 0,
                  visibility: isAnyProductSelected ? "visible" : "hidden",
                  transform: isAnyProductSelected ? "scale(1)" : "scale(0.9)",
                  transition: "0.2s",
                }}
              >
                <i className="bi bi-trash" style={{ fontSize: 16 }} />
                <span>Eliminar</span>
              </button>

              <button
                onClick={() => window.print()}
                style={{
                  ...buttonStyle,
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                  color: "#111",
                  fontWeight: 600,
                }}
              >
                <i className="bi bi-printer" />
                <span>Imprimir</span>
              </button>

              <button
                onClick={() => {
                  setIsModalOpen(true);
                  setEditingProduct(null);
                }}
                style={{
                  ...buttonStyle,
                  backgroundColor: "#007bff",
                  color: "#fff",
                  border: "none",
                  padding: "8px 14px",
                }}
              >
                <i className="bi bi-plus-lg" /> Agregar
              </button>
            </div>
          </div>

          {/* Modal Ajustar Precios */}
          <ProductButton
            isOpen={adjustOpen}
            onClose={() => setAdjustOpen(false)}
            products={products}
            selectedProducts={selectedProducts}
            productosCol={productosCol}
          />
        </div>

        <Sidebar />

        {isLoading ? (
          <Spinner />
        ) : filteredProducts.length === 0 ? (
          <div className="no-resultados">
            <div className="mensaje-sin-resultados" style={{ marginTop: 30 }}>
              <p
                style={{ textAlign: "center", margin: "20px 0", color: "#888" }}
              >
                No hay artículos disponibles.
              </p>
            </div>

            <div
              className="imagen-sin-resultados"
              style={{ display: "flex", justifyContent: "center" }}
            >
              <img
                src="/img/no-data.svg"
                alt="Sin datos"
                width={350}
                height={350}
              />
            </div>
          </div>
        ) : (
          <div className="printable-area">
            <h1 className="print-title">Artículos de Compra</h1>
            <ProductTable
              products={filteredProducts}
              selectedProducts={selectedProducts}
              setSelectedProducts={(sel) => setSelectedProducts(sel)}
              onSelectionChange={(sel) => setSelectedProducts(sel)}
              onEditProduct={(id) => {
                const p = products.find((x) => x.id === id);
                setEditingProduct(p);
                setIsModalOpen(true);
              }}
              onDeleteProduct={handleDeleteProduct}
              onCellClick={(id) => {
                const p = products.find((x) => x.id === id);
                setDetailProd(p);
                setDetailOpen(true);
              }}
              onDuplicateProduct={handleDuplicateProduct}
            />

            {detailOpen && detailProd && (
              <DetailsArticuloModal
                isOpen={true}
                onClose={() => {
                  setDetailOpen(false);
                  setDetailProd(null);
                }}
                product={detailProd}
                gallery={
                  detailProd.imagenes?.length
                    ? detailProd.imagenes
                    : detailProd.imagen
                    ? [detailProd.imagen]
                    : []
                }
                related={products.filter(
                  (x) =>
                    x.id !== detailProd.id &&
                    ((x.categoria ?? "").toString().trim().toLowerCase() ||
                      "ninguna") ===
                      ((detailProd.categoria ?? "")
                        .toString()
                        .trim()
                        .toLowerCase() || "ninguna")
                )}
              />
            )}
          </div>
        )}

        <AddProductModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={editingProduct ? handleEditProduct : handleAddProduct}
          editingProduct={editingProduct}
        />
      </div>
    </>
  );
};

export default ArticulosCompra;
